package com.crowpay.views.dialogs

import com.crowpay.Incident
import com.crowpay.IncidentMessage
import com.crowpay.LineItemCompletionMessage
import com.crowpay.completionResponse.CompletionFlowPermissions
import com.crowpay.completionResponse.CompletionResponse
import com.crowpay.completionResponse.serializable
import com.crowpay.incidentResponse.IncidentFlowPermissions
import com.crowpay.incidentResponse.IncidentResponseType
import com.crowpay.incidentResponse.serializable
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.AdjustedLineItem
import com.crowpay.views.components.files.resolveProjectAttachments
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.ViewWriter

fun WorkItemRequestSignOffDialog(
    items: List<AdjustedLineItem>,
    history: List<LineItemCompletionMessage>,
    dismiss: ViewWriter.(submitted: Boolean) -> Unit = { dialogScreenNavigator.dismiss() }
): TextAndFileInputDialog {
    require(items.isNotEmpty()) { "RequestSignOffDialog needs at least 1 LineItem" }

    val firstSignOff = history.none { completion -> completion.lineItem in items.map { it._id } }
    val project = items.map { it.project }.toSet().singleOrNull() ?: throw IllegalArgumentException("All line items must be from the same project")

    return TextAndFileInputDialog(
        "Request Sign-Off",
        if (firstSignOff)
            """
                Submitting for sign-off will send an approval request to the client. Ensure ${if (items.size > 1) "all items are" else "the item is"} fully complete and the client is aware of your proposal.
    
                If minor loose ends remain, notify the client they’ve been added to the punch list for project closeout.
    
                Tip: Include an explanation for why you're signing off along with pictures to help the client approve and release payment faster.
            """.trimIndent()
        else
            """
                Before resubmitting for approval, ensure the work is fully complete and meets the client’s expectations.
    
                If the client does not approve again, they may escalate the issue, which could lead to further delays and formal resolution steps. Resubmit only if you’ve addressed all feedback and communicated clearly with the client.
    
                Tips:
                - Attach updated pictures or documentation to help the client approve your work faster.
                - If small, low-value items remain (e.g., a sprinkler clock on backorder), communicate with the client and list them as punch list or warranty items. This reassures the client that loose ends will be resolved during project closeout.
            """.trimIndent(),
        "Request Sign-Off",
        hint = "Provide any additional context for this sign-off"
    ) { submission ->
        if (submission == null) {
            dismiss(false)
            return@TextAndFileInputDialog
        }

        val files = submission.files.resolveProjectAttachments()
        val session = notNullSession()
        val response =
            if (items.singleOrNull()?.let { CompletionFlowPermissions(it.wraps, false, history).mustRequestReviewOfRemedy } == true)
                CompletionResponse.Remedy.RemedyComplete(
                    history.asReversed().firstNotNullOf { it.responseType as? CompletionResponse.Remedy }
                )
            else
                CompletionResponse.RequestComplete(session.currentAccess())

        items
            .map {
                session.nonCached.lineItemCompletionMessage.sendMessage(
                    LineItemCompletionMessage(
                        project = project,
                        lineItem = it._id,
                        sender = session.userId,
                        clientMessage = false,
                        responseTypeSerializable = response.serializable(),
                        message = submission.message,
                        attachments = files
                    )
                )
            }
            .forEach(session.lineItemCompletionMessages::localInsert)

        dismiss(true)
    }
}

fun IncidentRequestSignOffDialog(incident: Incident, permissions: IncidentFlowPermissions): TextAndFileInputDialog =
    TextAndFileInputDialog(
        "Request Sign-Off",
        "Incident request sign-off dialog message",
        "Request Sign-Off",
        hint = "Provide any additional context for this sign-off"
    ) { submission ->
        if (submission == null) {
            dialogScreenNavigator.dismiss()
            return@TextAndFileInputDialog
        }

        val session = notNullSession()
        session.nonCached.incidentMessage.sendMessage(
            IncidentMessage(
                incident = incident._id,
                clientMessage = false,
                project = incident.project,
                sender = session.userId,
                typeSerializable =
                    if (permissions.mustRequestReview) IncidentResponseType.Remedy.RemedyHandled(
                        permissions.messages.asReversed().firstNotNullOf { it.type as? IncidentResponseType.Remedy }
                    ).serializable()
                    else IncidentResponseType.Handled().serializable(),
                attachments = submission.files.resolveProjectAttachments(),
                message = submission.message
            )
        )

        dialogScreenNavigator.dismiss()
    }